import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Shield, Key, AlertCircle, Loader2, ArrowLeft, Eye, EyeOff, Lock } from "lucide-react";
import { useAuthStore } from "../stores/auth";

export function Login() {
  const navigate = useNavigate();
  const { login, loginWithSession, isLoading, error, clearError } = useAuthStore();
  
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [secureMode, setSecureMode] = useState(true); // Default to secure session auth

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    
    if (!apiKey.trim()) {
      return;
    }

    const success = secureMode
      ? await loginWithSession(apiKey.trim())
      : await login(apiKey.trim());
    if (success) {
      navigate("/dashboard");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col">
      {/* Header */}
      <header className="p-6 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>
        <Link to="/signin" className="text-sm text-primary-600 hover:text-primary-700 font-medium">
          Not an enterprise user? →
        </Link>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-500 to-success-500 mb-4">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Enterprise Sign In</h1>
            <p className="text-slate-600 mt-2">Sign in with your API key to access the dashboard</p>
          </div>

          {/* Login form */}
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Error message */}
              {error && (
                <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-800">Authentication failed</p>
                    <p className="text-sm text-red-600 mt-1">{error}</p>
                  </div>
                </div>
              )}

              {/* API Key input */}
              <div>
                <label htmlFor="apiKey" className="block text-sm font-medium text-slate-700 mb-2">
                  API Key
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Key className="w-5 h-5 text-slate-400" />
                  </div>
                  <input
                    id="apiKey"
                    type={showKey ? "text" : "password"}
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="zkcn_live_..."
                    className="block w-full pl-10 pr-12 py-3 border border-slate-300 rounded-lg 
                             focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                             placeholder:text-slate-400 text-slate-900"
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowKey(!showKey)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600"
                  >
                    {showKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="mt-2 text-sm text-slate-500">
                  Enter your API key starting with <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">zkcn_live_</code>
                </p>
              </div>

              {/* Secure Session Toggle */}
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-slate-500" />
                  <div>
                    <p className="text-sm font-medium text-slate-700">Secure Session</p>
                    <p className="text-xs text-slate-500">
                      {secureMode
                        ? "API key exchanged for httpOnly cookie (recommended)"
                        : "API key stored in browser (less secure)"}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setSecureMode(!secureMode)}
                  aria-label="Toggle secure session mode"
                  className={`relative w-10 h-5 rounded-full transition-colors ${
                    secureMode ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                      secureMode ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {/* Submit button */}
              <button
                type="submit"
                disabled={isLoading || !apiKey.trim()}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 
                         bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300
                         text-white font-medium rounded-lg transition-colors"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  "Sign in"
                )}
              </button>
            </form>

            {/* Divider */}
            <div className="relative my-8">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-slate-500">Don't have an API key?</span>
              </div>
            </div>

            {/* Sign up link */}
            <Link
              to="/signup"
              className="w-full flex items-center justify-center gap-2 px-4 py-3 
                       border-2 border-slate-200 hover:border-primary-300 hover:bg-primary-50
                       text-slate-700 hover:text-primary-700 font-medium rounded-lg transition-colors"
            >
              Create an account
            </Link>
          </div>

          {/* Help text */}
          <div className="mt-8 text-center">
            <p className="text-sm text-slate-500">
              Need help? Check our{" "}
              <a href="https://docs.zkcn.org" className="text-primary-600 hover:underline">
                documentation
              </a>{" "}
              or{" "}
              <a href="mailto:support@zkcn.org" className="text-primary-600 hover:underline">
                contact support
              </a>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
